import { createServerFn } from "@tanstack/react-start";
import {
  DEFAULT_PRICING,
  DEFAULT_USAGE_LIMITS,
  PRICING_SETTINGS_KEYS,
  normalizePricingMap,
  normalizeUsageLimits,
  pickPricingValue,
  type PricingMap,
  type UsageLimits,
} from "./commercial-config";

export type PublicCommercialConfig = {
  pricing: PricingMap;
  limits: UsageLimits;
  /**
   * false quando o resolvedor estrito bloquearia uma operação financeira
   * (ex.: configuração Live num host local/Preview). A aplicação continua a
   * carregar; apenas as compras ficam desactivadas.
   */
  paymentsAvailable: boolean;
  /** Motivo seguro, sem segredos nem valores de tokens. */
  paymentsBlockedReason: string | null;
};

/**
 * LEITURA PÚBLICA — apenas textos, limites e etiquetas de preço.
 *
 * Nunca usa o resolvedor estrito de pagamentos: um host local/Preview com
 * `PAYMENTS_ENVIRONMENT=live` deixava esta função a lançar e a página em
 * branco. Aqui usamos o descritor seguro (Sandbox em local/Preview) e
 * devolvemos `paymentsAvailable: false` para desactivar as compras.
 */
export const getPublicCommercialConfig = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicCommercialConfig> => {
    const { describeServerDisplayPaymentEnvironment } = await import("@/lib/stripe.server");
    const display = describeServerDisplayPaymentEnvironment();
    if (!display.paymentsAvailable) {
      // O detalhe técnico completo fica nos logs/Operações, não na resposta.
      console.warn("[payments] display fallback:", display.reason);
    }

    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data, error } = await supabaseAdmin
        .from("app_settings")
        .select("key,value")
        .in("key", [...PRICING_SETTINGS_KEYS, "usage_limits"]);
      if (error) throw error;

      const settings = Object.fromEntries(
        (data ?? []).map((row: { key: string; value: unknown }) => [row.key, row.value]),
      );
      return {
        pricing: normalizePricingMap(pickPricingValue(settings, display.environment)),
        limits: normalizeUsageLimits(settings.usage_limits),
        paymentsAvailable: display.paymentsAvailable,
        paymentsBlockedReason: display.paymentsAvailable ? null : display.reason,
      };
    } catch (error) {
      console.error("[commercial-config] read failed:", error);
      return {
        pricing: DEFAULT_PRICING,
        limits: DEFAULT_USAGE_LIMITS,
        paymentsAvailable: false,
        paymentsBlockedReason: "Configuração comercial temporariamente indisponível.",
      };
    }
  },
);
